import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';

import { StorageService } from '../services/storage.service';
import { LanguageStateService } from '../services/language-state.service';

import { LIB_LANG } from '../config/language.config';

export type LanguageType =
  | 'en'
  | 'de'
  | 'es'
  | 'es-419'
  | 'fr'
  | 'it'
  | 'pl'
  | 'pt'
  | 'ru'
  | 'vi'
  | 'tr'
  | 'uk'
  | 'only-key';

@Injectable({ providedIn: 'root' })
export class LanguageFacade {
  private translate = inject(TranslateService);
  private locale = inject(BsLocaleService);
  private storage = inject(StorageService);
  private state = inject(LanguageStateService);

  private readonly supportedLanguages: { [key in LanguageType]: LanguageType } = {
    'en': 'en',
    'de': 'de',
    'es': 'es',
    'es-419': 'es-419',
    'fr': 'fr',
    'it': 'it',
    'pl': 'pl',
    'pt': 'pt',
    'ru': 'ru',
    'vi': 'vi',
    'tr': 'tr',
    'uk': 'uk',
    'only-key': 'only-key',
  };

  initApp() {
    const savedLanguage = this.storage.get('language') || 'en' as LanguageType;

    const lang: LanguageType =
      savedLanguage && this.isLanguageType(savedLanguage)
        ? savedLanguage
        : 'en';

    this.setLanguage(lang);

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === 'language' && event.newValue) {
          this.setLanguage(event.newValue);
        }
      });

      (window as any).setLocale = (lang: string) => {
        this.setLanguage(lang);
      };
    }
  }

  setLanguage(lang: string) {
    this.translate.use(lang);

    this.storage.set('language', lang);

    const locale = this.getLocale(lang);

    this.locale.use(locale);

    this.state.setLoaded();
  }

  use(lang: string) {
    this.setLanguage(lang);
  }

  isLanguageType(value: string): value is LanguageType {
    return value in this.supportedLanguages;
  }

  getLocale(lang: string): string {
    return (
      LIB_LANG.bootstrap[
        lang as keyof typeof LIB_LANG.bootstrap
      ] || LIB_LANG.bootstrap.en
    );
  }

  get(key: string | string[], params?: any) {
    return this.translate.get(key, params);
  }

  instant(key: string | string[], params?: any) {
    return this.translate.instant(key, params);
  }

  setTranslation(
    lang: string,
    translations: any,
    merge: boolean = true
  ) {
    this.translate.setTranslation(
      lang,
      translations,
      merge
    );
  }

  get onLangChange() {
    return this.translate.onLangChange;
  }

  get currentLang(): LanguageType {
    return this.translate.currentLang as LanguageType || 'en'
  }
}